import {
  CrossFrameMessenger,
  HandshakeMessage,
  InitializationOptions,
  InitializeMessage,
  MessageListener,
  VersionNumber,
} from '@tableau/api-internal-contract-js';

interface PendingInitializeMessage {
  iframe: Window;
  msg: InitializeMessage;
}

export type InitializationCompleteHandler = (
  apiVersion: VersionNumber,
  initializationMessageGuid: string,
  extensionFrame: Window,
  options?: InitializationOptions,
) => void;

/**
 * This class handles listening for an initialization message from an extension.
 * This also handles listening to the version handshake message from a sandboxed extension.
 */
export class InitializationHandler {
  // We cannot be sure we've initialized the right extension until we both have access to the extension frame
  // and the initialize messages. Keep a list of pending messages to check to avoid timing issues
  private _pendingInitializeMessages: Array<PendingInitializeMessage> = [];
  private _messageListener: MessageListener;
  private _extensionFrame?: Window;

  /**
   * Creates an instance of InitializationHandler.
   * @param thisWindow The window we are running in (we'll listen for messages to this window)
   * @param _onInitializationComplete Callback function once we've received an initialization message
   */
  public constructor(thisWindow: Window, private _onInitializationComplete: InitializationCompleteHandler) {
    // Create a messenger which will be used to listen to messages posted to our window
    this._messageListener = new CrossFrameMessenger(thisWindow);
    this._messageListener.setHandshakeMessageHandler(this.onHandshakeMessage.bind(this));
    this._messageListener.setInitializeMessageHandler(this.onInitializationMessage.bind(this));
    this._messageListener.startListening();
  }

  /**
   * Call this when the other frame where the extension is running has been received
   *
   * @param extensionFrame The frame holding the extension
   */
  public receivedExtensionFrame(extensionFrame: Window): void {
    // Save the extension frame and check to see if it sent us any initialization messages
    this._extensionFrame = extensionFrame;
    this.checkInitialization();
  }

  // TODO this initialization control flow has a smell to it. come back and review again.
  private checkInitialization(): void {
    if (this._extensionFrame) {
      // If we have a frame, we can see if we have a message from our frame yet
      let foundInitializationMessage = false;
      for (const pendingMessage of this._pendingInitializeMessages) {
        // For some reason in IE11 in a multitab workbook with extensions on multiple dashboards, old pending
        // initializeation messages from extensions on different dashboards than the current one stick around.
        // Accessing pendingMessage.iframe on one of these stale messages causes a 'Permission [or 'Access'] denied' error
        // TFS 875565
        try {
          foundInitializationMessage = pendingMessage.iframe === this._extensionFrame;
        } catch (err) {
          console.warn(err.message);
        }
        if (foundInitializationMessage) {
          // Stop listening and send off our notification
          this._messageListener.stopListening();
          this._messageListener.setInitializeMessageHandler(undefined);
          this._pendingInitializeMessages = [];
          this._onInitializationComplete(
            pendingMessage.msg.apiVersion,
            pendingMessage.msg.msgGuid,
            this._extensionFrame,
            pendingMessage.msg.options,
          );
          break;
        }
      }
    }
  }

  private onInitializationMessage(message: InitializeMessage, source: Window): void {
    // Save off the message and source and check to see if we have been fully initialized
    this._pendingInitializeMessages.push({
      iframe: source,
      msg: message,
    });

    this.checkInitialization();
  }

  private onHandshakeMessage(message: HandshakeMessage, source: Window): void {
    if (source && source === this._extensionFrame) {
      // other window & other window origin are not initialized until after initializeAsync() is called from an extension.
      // Handshake and ACK happen prior to that.
      this._messageListener.setOtherWindow(source);
      this._messageListener.setOtherWindowOrigin('*');

      const preparedMessage = this._messageListener.prepareAckMessage();
      preparedMessage.send();

      this._messageListener.setHandshakeMessageHandler(undefined);
    }
  }
}
